import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { featureAccess } from "../../permissions/access";
import { useUserRole } from "../../utils/hooks/useUserRole";
import BranchTable from "./BranchTable";
import PalletBreadcrumbs from "./PalletBreadcrumbs";
import PalletFilters from "./PalletFilters";
import PalletSummaryCards from "./PalletSummaryCards";
import RouteTable from "./RouteTable";
import TripTable from "./TripTable";
import {
  MAX_RANGE_DAYS,
  PALLET_SUMMARY_LEVEL,
  PALLET_SUMMARY_TITLE,
} from "./constants";
import { isRangeTooLong, routeLabel } from "./palletSummary";
import { usePalletSummary } from "./usePalletSummary";
import { usePalletSummaryScope } from "./usePalletSummaryScope";

const RANGE_TOO_LONG_TEXT = `เลือกช่วงวันที่ได้ไม่เกิน ${MAX_RANGE_DAYS} วัน กรุณาเลือกช่วงให้สั้นลง`;
const LOAD_FAILED_TEXT = "โหลดข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";

/**
 * สรุปถาดขึ้น–ถาดกลับ — the trays a van took out at the morning check-in against the trays
 * counted back at its reconcile, drilled from every branch to one branch's routes to one
 * route's trips.
 *
 * Head office and master open on every branch. Everyone else opens on their own branch's
 * routes with no branch picker; the server pins them to that branch whatever the URL says.
 */
export default function PalletSummary() {
  const role = useUserRole();
  const canPickBranch = featureAccess.palletSummaryAllBranches(role);
  const canWriteNote = featureAccess.palletNoteWrite(role);
  const {
    level,
    startDay,
    endDay,
    branchCode,
    routeUuid,
    changeDates,
    selectBranch,
    selectRoute,
  } = usePalletSummaryScope(canPickBranch);
  const isRangeAllowed = !isRangeTooLong(startDay, endDay);
  const { branches, routes, trips, reload } = usePalletSummary({
    level,
    startDay,
    endDay,
    branchCode,
    routeUuid,
    isRangeAllowed,
  });

  // The cards and the table both read the response of the level on screen, and only that one.
  const levelResponse = {
    [PALLET_SUMMARY_LEVEL.overview]: branches,
    [PALLET_SUMMARY_LEVEL.branch]: routes,
    [PALLET_SUMMARY_LEVEL.route]: trips,
  }[level];

  const canPickRoute = level !== PALLET_SUMMARY_LEVEL.overview;
  const routeOptions = routes.data?.rows ?? [];
  const branchName = routes.data?.branch.branch_name ?? branchCode ?? "";
  const routeName = trips.data ? routeLabel(trips.data.route) : "";

  const handleOpenOverview = () => selectBranch(null);
  const handleOpenBranch = () => selectRoute(null);

  const renderLevelTable = () => {
    if (level === PALLET_SUMMARY_LEVEL.overview) {
      return branches.data && <BranchTable report={branches.data} onOpenBranch={selectBranch} />;
    }
    if (level === PALLET_SUMMARY_LEVEL.branch) {
      return routes.data && <RouteTable report={routes.data} onOpenRoute={selectRoute} />;
    }
    return (
      trips.data && (
        <TripTable report={trips.data} canWriteNote={canWriteNote} onNoteSaved={reload} />
      )
    );
  };

  return (
    <Stack sx={{ py: 1 }}>
      <Typography variant="h6" mb={1}>
        {PALLET_SUMMARY_TITLE}
      </Typography>
      <PalletBreadcrumbs
        level={level}
        hasOverview={canPickBranch}
        branchName={branchName}
        routeName={routeName}
        onOpenOverview={handleOpenOverview}
        onOpenBranch={handleOpenBranch}
      />
      <PalletFilters
        startDay={startDay}
        endDay={endDay}
        onChangeDates={changeDates}
        canPickBranch={canPickBranch}
        branchCode={branchCode}
        onSelectBranch={selectBranch}
        canPickRoute={canPickRoute}
        routeOptions={routeOptions}
        routeUuid={routeUuid}
        onSelectRoute={selectRoute}
      />
      {!isRangeAllowed && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {RANGE_TOO_LONG_TEXT}
        </Alert>
      )}
      {levelResponse.isLoading && (
        <Stack alignItems="center" py={6}>
          <CircularProgress />
        </Stack>
      )}
      {levelResponse.isFailed && <Alert severity="error">{LOAD_FAILED_TEXT}</Alert>}
      {levelResponse.data && (
        <>
          <PalletSummaryCards totals={levelResponse.data.summary} />
          {renderLevelTable()}
        </>
      )}
    </Stack>
  );
}
